import Button from '../../common/Button/Button.component'
import InputField from '../../common/InputField/InputField.component'
import MaskedInput from 'react-maskedinput'
import { MdLockOutline } from 'react-icons/md'
import { MedicareQuotesType } from '../../../enums/MedicareQuoteTypeEnum'
import PhoneConsentDisclaimer from '../../common/PhoneConsentDisclaimer.component'
import RadioGroup from '../../common/RadioGroup/RadioGroup.component'
import React from 'react'
import StringConstants from '../../../constants/StringConstants'
import ValidationUtils from '../../../utils/validation/ValidationUtils'
import customerStore from '../../../datastore/CustomerStore'
import { fireEvent } from '../../../cra'
import styled from '@emotion/styled'
import theme from '../../../global/theme'
import { toIdSafeString } from '../../../utils/StringUtils'
import { updateCustomerDetails } from '../../../service/customer'
import { upsertPreLeadObject } from '../../../service/preLeadService'

interface ModelProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  onEnroll: () => void
  onCancelWithPhone?: () => void
  planName: string
  planType: MedicareQuotesType
  planId: string
  buttonLoading: boolean
  preferredPhoneNumber?: string
  setPreferredPhoneNumber?: (data: string) => void
  enrollmentRedirectURL?: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  .highlight {
    color: ${theme.colors.secondary};
    font-size: 24px;
    font-weight: 700;
  }

  .continue-disclaimer {
    font-size: 14px;
    background-color: ${theme.colors.lightBack};
    padding: 10px;
    border-radius: 8px;
  }

  h2 {
    color: #272f69;
  }
  p {
    padding-bottom: 10px;
    font-weight: 600;
    color: ${theme.colors.textGrey};
  }

  .buttons {
    display: flex;
    gap: 10px;
    justify-content: space-between;
  }

  .privacy-disclaimer {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${theme.colors.textGrey};
  }
`

const PrivacyDisclaimer: React.FC = () => (
  <p className='privacy-disclaimer'>
    <MdLockOutline size={14} />
    We keep your information private and secure.
  </p>
)

const EnrollmentRequestConfirmation: React.FC<ModelProps> = ({
  setOpen,
  onEnroll,
  onCancelWithPhone,
  planName,
  planType,
  planId,
  buttonLoading,
  preferredPhoneNumber,
  setPreferredPhoneNumber,
}) => {
  const formatPhoneNumber = (phoneNumber: string): string => {
    phoneNumber = phoneNumber.replace('+1', '')
    const areaCode = phoneNumber.slice(0, 3)
    const firstPart = phoneNumber.slice(3, 6)
    const secondPart = phoneNumber.slice(6)
    return `(${areaCode}) ${firstPart}-${secondPart}`
  }

  const storedPhone = customerStore.get().phone?.replace('+1', '') ?? ''
  const storedEmail = customerStore.get().email ?? ''
  const isMissingContactInfo = !storedPhone || !storedEmail

  const enrollLabel =
    planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
      ? 'application'
      : 'enrollment'
  const planActionLabel =
    planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
      ? 'Application'
      : 'Enrollment'

  // ─── Normal flow state ────────────────────────────────────────────────────
  const [isPreferredNumber, setIsPreferredNumber] = React.useState(false)
  const [hasPreferredNumber, setHasPreferredNumber] = React.useState('')
  const [preferredPhoneValue, setPreferredPhoneValue] = React.useState('')
  const [altPhoneState, setAltPhoneState] = React.useState({
    alternatePhoneError: false,
    alternatePhoneHelperText: '',
  })

  const validatePreferredPhone = (alternatePhone: string) => {
    var alternatePhoneError = true
    ValidationUtils.verifyPhoneNumber(alternatePhone)
      .then((response: any) => {
        if (response.data.data.status === 'valid') {
          alternatePhoneError = false
          setAltPhoneState({
            alternatePhoneError:
              alternatePhoneError || storedPhone === alternatePhone,
            alternatePhoneHelperText: alternatePhoneError
              ? StringConstants.UI_ERROR_MSG_FOR_INVALID_ALTERNATE_PHONE
              : storedPhone === alternatePhone
                ? StringConstants.UI_ERROR_MSG_FOR_PREFERRED_PHONE_SAME_AS_PHONE
                : '',
          })
        }
      })
      .catch((err: Error) => console.log(err))
    setAltPhoneState({
      alternatePhoneError:
        alternatePhoneError || storedPhone === alternatePhone,
      alternatePhoneHelperText: alternatePhoneError
        ? StringConstants.UI_ERROR_MSG_FOR_INVALID_ALTERNATE_PHONE
        : storedPhone === alternatePhone
          ? StringConstants.UI_ERROR_MSG_FOR_PREFERRED_PHONE_SAME_AS_PHONE
          : '',
    })
  }

  const handlePreferredPhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const alternatePhone = e.target.value.replace(/[- )( _]/g, '')
    validatePreferredPhone(alternatePhone)
    setPreferredPhoneValue(alternatePhone)
    setPreferredPhoneNumber && setPreferredPhoneNumber(alternatePhone)
  }

  const handlePreferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasPreferredNumber(e.target.value)
    setIsPreferredNumber(e.target.value === 'No')
  }

  // ─── Missing contact info state ───────────────────────────────────────────
  const [contactPhone, setContactPhone] = React.useState(storedPhone)
  const [contactEmail, setContactEmail] = React.useState(storedEmail)
  const [contactLoading, setContactLoading] = React.useState(false)
  const [contactInfoJustSubmitted, setContactInfoJustSubmitted] =
    React.useState(false)
  const [contactErrors, setContactErrors] = React.useState({
    phone: false,
    phoneText: '',
    email: false,
    emailText: '',
  })

  const validateContactPhone = (phone: string) => {
    const isValid = phone.replace(/\D/g, '').length === 10
    setContactErrors((prev) => ({
      ...prev,
      phone: !isValid,
      phoneText:
        phone && !isValid ? 'Please enter a valid 10-digit phone number' : '',
    }))
    return isValid
  }

  const validateContactEmail = (email: string) => {
    const isValid = ValidationUtils.validateEmail(email)
    setContactErrors((prev) => ({
      ...prev,
      email: !isValid,
      emailText: email && !isValid ? 'Please enter a valid email address' : '',
    }))
    return isValid
  }

  const handleContactSubmit = async () => {
    const phoneValid = validateContactPhone(contactPhone)
    const emailValid = validateContactEmail(contactEmail)
    if (!phoneValid || !emailValid) return

    setContactLoading(true)
    const fullPhone = '+1' + contactPhone.replace(/\D/g, '')
    try {
      await updateCustomerDetails({ email: contactEmail, phone: fullPhone })
      customerStore.set({ email: contactEmail, phone: fullPhone })
      upsertPreLeadObject({ email: contactEmail, phone: fullPhone })
      setPreferredPhoneNumber &&
        setPreferredPhoneNumber(contactPhone.replace(/\D/g, ''))
      setContactInfoJustSubmitted(true)
      onEnroll()
    } catch {
      // Error snackbar already shown by updateCustomerDetails service
    } finally {
      setContactLoading(false)
    }
  }

  // ─── Missing contact info mode ────────────────────────────────────────────
  if (isMissingContactInfo || contactInfoJustSubmitted) {
    return (
      <Container>
        <h2>One more step to enroll</h2>
        <p>
          Enter your details below so we can save your plan choices and send you
          everything you need to finalize your enrollment.
        </p>
        <InputField
          error={contactErrors.phone}
          helperText={contactErrors.phoneText}
          label='Phone Number'
          width='100%'
          value={contactPhone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value.replace(/[- )( _]/g, '')
            setContactPhone(val)
            validateContactPhone(val)
            setPreferredPhoneNumber && setPreferredPhoneNumber(val)
          }}
          inputComponent={MaskedInput}
          inputProps={{ mask: '(111) 111-1111' }}
          placeholder='(123) 456-7890'
          disabled={contactLoading || buttonLoading}
        />
        <InputField
          error={contactErrors.email}
          helperText={contactErrors.emailText}
          label='Email Address'
          width='100%'
          value={contactEmail}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setContactEmail(e.target.value)
            validateContactEmail(e.target.value)
          }}
          placeholder='you@example.com'
          disabled={contactLoading || buttonLoading}
        />
        <PrivacyDisclaimer />
        <p className='continue-disclaimer'>
          By continuing, you'll begin the {enrollLabel} process for{' '}
          <span style={{ color: theme.colors.secondary }}>{planName}</span>.
        </p>
        <div className='buttons'>
          <Button
            variant='contained'
            bold={false}
            color='secondary'
            onClick={handleContactSubmit}
            loading={contactLoading || buttonLoading}
            disabled={
              contactLoading ||
              buttonLoading ||
              !contactPhone ||
              !contactEmail ||
              contactErrors.phone ||
              contactErrors.email
            }
            fontSize='14px'
            description={`Requested ${planActionLabel} in ${planName}`}
            id={toIdSafeString(`confirmEnroll ${planName}`)}
          >
            Save & Continue
          </Button>
          <Button
            color='info'
            bold={false}
            onClick={() => setOpen(false)}
            disabled={contactLoading || buttonLoading}
            fontSize='14px'
            description={`Cancelled ${planActionLabel} in ${planName}`}
          >
            Cancel
          </Button>
        </div>
        {!storedPhone && <PhoneConsentDisclaimer buttonLabel='Save & Continue' />}
      </Container>
    )
  }

  // ─── Normal mode ──────────────────────────────────────────────────────────
  return (
    <Container>
      <h2>One more step to enroll</h2>
      <p>
        We want to make sure we contact you at the right number. Is your
        existing phone number the preferred one for us to call?
      </p>
      <p className='highlight'>
        {formatPhoneNumber(customerStore.get().phone)}
      </p>
      <RadioGroup
        radioButtons={['Yes', 'No']}
        row={true}
        value={hasPreferredNumber}
        radioGroupName='Gender'
        onChange={handlePreferenceChange}
        extraPaddingBottom='5px'
        disabledValues={buttonLoading ? ['Yes', 'No'] : []}
      />
      {isPreferredNumber && (
        <InputField
          error={altPhoneState.alternatePhoneError}
          helperText={altPhoneState.alternatePhoneHelperText}
          label='Enter your Preferred Phone Number'
          width='100%'
          value={preferredPhoneValue}
          onChange={handlePreferredPhoneChange}
          inputComponent={MaskedInput}
          inputProps={{ mask: '(111) 111-1111' }}
          shrink={preferredPhoneNumber ? true : undefined}
          placeholder='(123) 456-7890'
          disabled={buttonLoading}
        />
      )}
      <PrivacyDisclaimer />
      <p>
        Hit "Continue" to confirm your interest in {enrollLabel}
        {' for '}
        <div>
          <b>{planName}!</b>
        </div>
      </p>
      <div className='buttons'>
        <Button
          variant='contained'
          bold={false}
          color='secondary'
          onClick={(e) => {
            fireEvent(
              planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
                ? 'confirmApply'
                : 'confirmEnroll',
              e,
              {
                description: `Confirmed ${planActionLabel} in ${planName}`,
                planType,
                planName,
                planId,
              },
            )
            upsertPreLeadObject({
              phone: preferredPhoneNumber || customerStore.get().phone,
              email: customerStore.get().email,
            })
            onEnroll()
          }}
          loading={buttonLoading}
          disabled={
            buttonLoading ||
            hasPreferredNumber === '' ||
            (hasPreferredNumber === 'No' &&
              (preferredPhoneNumber === '' ||
                altPhoneState.alternatePhoneError ||
                preferredPhoneValue === ''))
          }
          fontSize='14px'
          description={`Requested ${planActionLabel} in ${planName}`}
          id={toIdSafeString(`confirmEnroll ${planName}`)}
        >
          Save & Continue
        </Button>
        <Button
          color='info'
          bold={false}
          onClick={(e) => {
            fireEvent(
              planType === MedicareQuotesType.MEDICARE_SUPPLEMENT
                ? 'rejectApply'
                : 'rejectEnroll',
              e,
              {
                description: `Rejected ${planActionLabel} in ${planName}`,
                planType,
                planName,
                planId,
              },
            )
            onCancelWithPhone?.()
            setOpen(false)
          }}
          disabled={buttonLoading}
          fontSize='14px'
          description={`Cancelled ${planActionLabel} in ${planName}`}
        >
          Cancel
        </Button>
      </div>
      {isPreferredNumber && (
        <PhoneConsentDisclaimer buttonLabel='Save & Continue' />
      )}
    </Container>
  )
}

export default EnrollmentRequestConfirmation
